import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useSpring, useTransform } from 'framer-motion';
import { MapPin, Calendar, Briefcase } from 'lucide-react';
import SectionHeading from './ui/SectionHeading';
import StarBorder from './ui/StarBorder';

interface ExperienceEntry {
  title: string;
  company: string;
  location: string;
  period: string;
  type: string;
  achievements: string[];
  accent: string;
}

const experiences: ExperienceEntry[] = [
  {
    title: 'Jr. Integration Specialist',
    company: 'Abacus Consulting',
    location: 'Lahore, Pakistan',
    period: 'Oct 2025 – Present',
    type: 'Full-Time',
    achievements: [
      'Tested and validated 50+ API proxies across development and pre-production environments, ensuring migration readiness, policy compliance, and backend connectivity.',
      'Supported migration of ~50 APIs between Apigee organizations, performing revision comparisons, configuration validation, and issue identification to reduce post-migration defects.',
      'Led troubleshooting of a critical connectivity issue where a Cloud Run service failed to connect to on-prem and Cloud SQL databases; identified and resolved subnet misconfiguration to restore secure communication.',
      'Contributed to API platform R&D and infrastructure validation across Apigee X/Hybrid and Google Cloud, resolving proxy parsing errors, timeout misconfigurations, and IAM access issues.',
    ],
    accent: '#667eea',
  },
  {
    title: 'Backend / ML Engineer',
    company: 'Freelancer',
    location: 'Lahore, Pakistan',
    period: 'Jun 2025 – Sept 2025',
    type: 'Remote',
    achievements: [
      'Built CyberGuard AI, an AI-powered malware detection system with a Django REST backend and Random Forest ML model (96%+ accuracy).',
      'Developed secure APIs for file scanning and real-time risk assessment of Java bytecode.',
    ],
    accent: '#a78bfa',
  },
  {
    title: 'Project Cofounder (Remote) / Web Developer',
    company: 'EnergyCove',
    location: 'Multan, Pakistan',
    period: 'Jul 2024 – Present',
    type: 'Remote',
    achievements: [
      'Designed and implemented a web-based solar system quotation generator using Django and Python.',
      'Developed a system to scrape user electricity bills, generate recommended systems, and generate a PDF with a detailed cost breakdown of the new system.',
      'Reduced invoice generation times from days to within 10 seconds.',
    ],
    accent: '#34d399',
  },
];

/** A single timeline entry: glowing node on the rail + detail card. */
const TimelineItem: React.FC<{ exp: ExperienceEntry; index: number }> = ({ exp, index }) => {
  const itemRef = useRef(null);
  const isInView = useInView(itemRef, { once: true, margin: '-120px' });

  return (
    <div ref={itemRef} className="relative flex gap-6 md:gap-10">
      {/* Node on the rail */}
      <div className="flex-shrink-0 w-14 md:w-16 flex justify-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : {}}
          transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.15 }}
          className="sticky top-32 z-10 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-primary-500 to-secondary-500"
          style={{ boxShadow: `0 0 24px ${exp.accent}66, 0 0 60px ${exp.accent}33` }}
        >
          <Briefcase className="w-6 h-6" />
        </motion.div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="flex-1 pb-16"
      >
        <div className="glass-effect rounded-2xl p-6 md:p-8 hover:shadow-2xl hover:shadow-primary-500/10 transition-shadow duration-300">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-1">{exp.title}</h3>
              <h4 className="text-lg md:text-xl font-semibold mb-2" style={{ color: exp.accent }}>
                {exp.company}
              </h4>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
                <span className="flex items-center"><MapPin className="w-4 h-4 mr-1.5" />{exp.location}</span>
                <span className="flex items-center"><Calendar className="w-4 h-4 mr-1.5" />{exp.period}</span>
              </div>
            </div>
            <div className="flex-shrink-0">
              {index === 0 ? (
                <StarBorder color={exp.accent} speed="3s">
                  <span className="text-xs text-primary-400 font-medium">{exp.type}</span>
                </StarBorder>
              ) : (
                <span className="inline-block bg-white/5 border border-white/10 text-gray-300 px-3 py-1 rounded-full text-xs">
                  {exp.type}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {exp.achievements.map((achievement, achIndex) => (
              <motion.div
                key={achIndex}
                className="flex items-start space-x-3"
                initial={{ opacity: 0, x: -16 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + achIndex * 0.1, duration: 0.5 }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0"
                  style={{ backgroundColor: exp.accent }}
                />
                <p className="text-gray-300 text-sm md:text-base leading-relaxed">{achievement}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Width of the spiral band in px and vertical distance per half-turn
const RAIL_WIDTH = 44;
const SPIRAL_LOOP_HEIGHT = 150;

/**
 * Builds a helix-profile path: smooth cubic curves weaving left and right
 * around the rail's center, in pixel coordinates so the comet orb can ride
 * the exact same path via CSS offset-path.
 */
function buildSpiralPath(height: number, width: number): string {
  const cx = width / 2;
  const amp = width / 2 - 4;
  const halfTurns = Math.max(2, Math.round(height / SPIRAL_LOOP_HEIGHT));
  const seg = height / halfTurns;

  let d = `M ${cx} 0`;
  for (let i = 0; i < halfTurns; i++) {
    const y0 = i * seg;
    const y1 = (i + 1) * seg;
    const dir = i % 2 === 0 ? 1 : -1;
    d += ` C ${(cx + dir * amp).toFixed(1)} ${(y0 + seg * 0.3).toFixed(1)},`
       + ` ${(cx + dir * amp).toFixed(1)} ${(y1 - seg * 0.3).toFixed(1)},`
       + ` ${cx} ${y1.toFixed(1)}`;
  }
  return d;
}

const Experience: React.FC = () => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [railHeight, setRailHeight] = useState(0);

  // Re-measure the rail so the spiral spans the full timeline at any viewport size
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const measure = () => setRailHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const spiralPath = railHeight > 0 ? buildSpiralPath(railHeight, RAIL_WIDTH) : '';

  // Path draws as the timeline passes through the viewport center
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start 0.65', 'end 0.65'],
  });
  const drawProgress = useSpring(scrollYProgress, { stiffness: 90, damping: 25, restDelta: 0.001 });
  const cometDistance = useTransform(drawProgress, (v) => `${Math.min(Math.max(v, 0), 1) * 100}%`);
  const cometOpacity = useTransform(drawProgress, [0, 0.02, 0.98, 1], [0, 1, 1, 0]);

  return (
    <section id="experience" className="section-padding px-6 bg-gradient-to-b from-dark-100/20 to-transparent">
      <div className="max-w-5xl mx-auto">
        <SectionHeading eyebrow="Career" title="Work Experience" />

        <div ref={timelineRef} className="relative">
          {/* Spiral timeline rail */}
          <div
            ref={railRef}
            className="absolute left-7 md:left-8 top-0 bottom-16 -translate-x-1/2 pointer-events-none"
            style={{ width: RAIL_WIDTH }}
          >
            {spiralPath && (
              <>
                <svg
                  className="absolute inset-0 overflow-visible"
                  width={RAIL_WIDTH}
                  height={railHeight}
                  viewBox={`0 0 ${RAIL_WIDTH} ${railHeight}`}
                  fill="none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="timeline-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#667eea" />
                      <stop offset="0.5" stopColor="#a78bfa" />
                      <stop offset="1" stopColor="#34d399" />
                    </linearGradient>
                  </defs>
                  {/* Dim base spiral */}
                  <path d={spiralPath} stroke="rgba(255,255,255,0.07)" strokeWidth="2" />
                  {/* Scroll-drawn glowing spiral */}
                  <motion.path
                    d={spiralPath}
                    stroke="url(#timeline-grad)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    style={{ pathLength: drawProgress }}
                  />
                </svg>

                {/* Comet orb riding the spiral via CSS motion path */}
                <motion.div
                  className="absolute top-0 left-0 w-3.5 h-3.5 rounded-full bg-white"
                  style={{
                    offsetPath: `path("${spiralPath}")`,
                    offsetDistance: cometDistance,
                    offsetRotate: '0deg',
                    opacity: cometOpacity,
                    boxShadow: '0 0 10px #a78bfa, 0 0 28px #667eea, 0 0 50px rgba(102,126,234,0.5)',
                  }}
                />
              </>
            )}
          </div>

          {experiences.map((exp, index) => (
            <TimelineItem key={index} exp={exp} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
